import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import AppContainer from './AppContainer';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from './actions/alert.actions';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PrivateRoute from './components/Route/PrivateRoute';
import PublicRoute from './components/Route/PublicRoute';
import GlobalModal from 'components/Modal/GlobalModal';
import { navigationRef } from './helpers/navigation';

// Import ErrorBoundary directly (not lazy) since it needs to catch errors from lazy components
import ErrorBoundary from './components/Base/ErrorBoundary';

// Lazy load heavy components for better performance
const ErrorPage = React.lazy(() => import('./routing/ErrorPage'));
const NotFoundPage = React.lazy(() => import('./routing/NotFoundPage'));
const Briks = React.lazy(() => import('./routing/Briks'));
const AppInfoPage = React.lazy(() => import('./routing/AppInfoPage'));
const CustomerPage = React.lazy(() => import('./routing/CustomerPage'));
const GroupPage = React.lazy(() => import('./routing/CustomerPage/Customer/Group'));
const UserStatus = React.lazy(() => import('./routing/CustomerPage/UserStatus'));
const CustomerBrikView = React.lazy(() => import('./routing/CustomerPage/Brik/view'));
const CustomerBrikContent = React.lazy(() => import('./routing/CustomerPage/Brik/contents'));
const LoginPage = React.lazy(() => import('./routing/LoginPage'));
const ForgotPassword = React.lazy(() => import('./routing/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./routing/ResetPassword'));
const ProfileSettings = React.lazy(() => import('./routing/ProfileSettings'));
const BrikContentPage = React.lazy(() => import('./routing/BrikContentPage'));
const Dashboard = React.lazy(() => import('./routing/Dashboard'));
const BrikDetailsPage = React.lazy(() => import('./routing/BrikDetailsPage'));
const SubcriptionExpired = React.lazy(() => import('./routing/SubcriptionExpired'));
const SubcriptionSuccess = React.lazy(() => import('./routing/SubcriptionSuccess'));
const SubcriptionFail = React.lazy(() => import('./routing/SubcriptionFail'));

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '16px',
    color: '#666'
  }}>
    Loading...
  </div>
);

const AppRoutes = () => {
  const dispatch = useDispatch();

  const alert = useSelector((state: any) => state.alert);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    navigationRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    const { type = '', message = '' } = alert;
    if (type && message) {
      // Dismiss any still-showing toast so only the latest alert is visible.
      toast.dismiss();
      (toast as any)[type.toLowerCase()](message);
    }
  }, [alert]);

  useEffect(() => {
    dispatch(alertActions.clear());
  }, [location, dispatch]);

  return (
    <div>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/briks" element={<PrivateRoute><Briks /></PrivateRoute>} />
          <Route path="/brik/detail/:id" element={<PrivateRoute><BrikDetailsPage /></PrivateRoute>} />
          <Route path="/brik/:id/content/:contentId/type/:type" element={<PrivateRoute><BrikContentPage /></PrivateRoute>} />
          <Route path="/app-info" element={<PrivateRoute><AppInfoPage /></PrivateRoute>} />
          <Route path="/customer" element={<PrivateRoute><CustomerPage /></PrivateRoute>} />
          <Route path="/customer/group/:id" element={<PrivateRoute><GroupPage /></PrivateRoute>} />
          <Route path="/customer/user-status/:id" element={<PrivateRoute><UserStatus /></PrivateRoute>} />
          <Route path="/customer/birk/view/:id" element={<PrivateRoute><CustomerBrikView /></PrivateRoute>} />
          <Route path="/customer/brik/content/:id" element={<PrivateRoute><CustomerBrikContent /></PrivateRoute>} />
          <Route path="/login" element={<PublicRoute restricted><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute restricted><LoginPage /></PublicRoute>} />
          <Route path="/subcription-expired" element={<PublicRoute restricted><SubcriptionExpired /></PublicRoute>} />
          <Route path="/subcription-success" element={<PrivateRoute><SubcriptionSuccess /></PrivateRoute>} />
          <Route path="/subcription-fail" element={<PrivateRoute><SubcriptionFail /></PrivateRoute>} />
          <Route path="/forgot-password" element={<PublicRoute restricted={false}><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute restricted={false}><ResetPassword /></PublicRoute>} />
          <Route path="/profile-settings" element={<PrivateRoute><ProfileSettings /></PrivateRoute>} />
          <Route path="/error" element={<PublicRoute restricted={false}><ErrorPage /></PublicRoute>} />
          <Route path="*" element={<PublicRoute restricted={false}><NotFoundPage /></PublicRoute>} />
        </Routes>
      </Suspense>
    </div>
  );
};

const routing = function createRouting() {
  return (
    <>
      <ToastContainer />
      <Router>
        <GlobalModal />
        <AppContainer>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </AppContainer>
      </Router>
    </>
  );
};

export default routing;
